import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tooltip,
  IconButton,
  FormHelperText
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

type DepartmentType = 'tech' | 'sales' | 'admin';
type ScopeType = 'job_grade' | 'job_title';
type OverrideSource = 'base' | 'job_grade' | 'job_title';

interface ResolvedItem {
  item_id: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  is_free_item: boolean;
  base_weight: number;
  weight: number;
  is_enabled: boolean;
  base_requires_goal: boolean;
  requires_goal: boolean;
  weight_source: OverrideSource;
  enabled_source: OverrideSource;
  requires_goal_source: OverrideSource;
  job_grade_override_id: string | null;
  job_title_override_id: string | null;
}

interface ResolvedSet {
  department_type: string;
  scope_type: ScopeType;
  scope_value: string;
  job_grade: string | null;
  items: ResolvedItem[];
  total_weight: number;
  base_total_weight: number;
  enabled_items: number;
  weight_warning: string | null;
}

interface JobTitle {
  job_title_name: string;
  job_grade: string;
  department_types: DepartmentType[];
}

const JOB_GRADES = ['部長', '課長', '班長', '役職なし'];
const ALLOWED_WEIGHTS = [5, 10, 20];

const SOURCE_LABELS: Record<OverrideSource, string> = {
  base: '部門共通',
  job_grade: '職級で上書き',
  job_title: '役職で上書き'
};

interface Props {
  departmentType: DepartmentType;
}

const ItemOverridePanel: React.FC<Props> = ({ departmentType }) => {
  const [scopeType, setScopeType] = useState<ScopeType>('job_title');
  const [scopeValue, setScopeValue] = useState('');
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [resolvedSet, setResolvedSet] = useState<ResolvedSet | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 部署タイプが変わったら選択をリセットする
  useEffect(() => {
    setScopeValue('');
    setResolvedSet(null);
    setError(null);
  }, [departmentType]);

  useEffect(() => {
    const fetchJobTitles = async () => {
      try {
        const response = await api.get('/job-titles', {
          params: { department_type: departmentType }
        });
        setJobTitles(response.data.data);
      } catch (err) {
        console.error('役職マスタ取得エラー:', err);
        setError(extractErrorMessage(err, '役職マスタの取得に失敗しました'));
      }
    };
    fetchJobTitles();
  }, [departmentType]);

  const fetchResolvedSet = useCallback(async () => {
    if (!scopeValue) {
      setResolvedSet(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/evaluation-item-overrides', {
        params: {
          department_type: departmentType,
          scope_type: scopeType,
          scope_value: scopeValue
        }
      });
      setResolvedSet(response.data.data);
    } catch (err) {
      console.error('項目セット取得エラー:', err);
      setError(extractErrorMessage(err, '項目セットの取得に失敗しました'));
      setResolvedSet(null);
    } finally {
      setLoading(false);
    }
  }, [departmentType, scopeType, scopeValue]);

  useEffect(() => {
    fetchResolvedSet();
  }, [fetchResolvedSet]);

  /** この適用範囲での上書き行のID（無ければ null） */
  const ownOverrideId = (item: ResolvedItem): string | null =>
    scopeType === 'job_grade' ? item.job_grade_override_id : item.job_title_override_id;

  /** この適用範囲で現在指定されている目標必須の上書き値（未指定なら null＝継承） */
  const ownRequiresGoal = (item: ResolvedItem): boolean | null => {
    const isOwnScope =
      scopeType === 'job_grade'
        ? item.requires_goal_source === 'job_grade'
        : item.requires_goal_source === 'job_title';
    return isOwnScope ? item.requires_goal : null;
  };

  const saveOverride = async (
    item: ResolvedItem,
    changes: { weight?: number | null; is_enabled?: boolean; requires_goal?: boolean | null }
  ) => {
    setSavingItemId(item.item_id);
    setError(null);
    try {
      const response = await api.put('/evaluation-item-overrides', {
        item_id: item.item_id,
        scope_type: scopeType,
        scope_value: scopeValue,
        weight: changes.weight !== undefined ? changes.weight : item.weight,
        is_enabled: changes.is_enabled !== undefined ? changes.is_enabled : item.is_enabled,
        requires_goal:
          changes.requires_goal !== undefined ? changes.requires_goal : ownRequiresGoal(item)
      });
      await fetchResolvedSet();
      if (response.data.weight_warning) {
        setError(response.data.weight_warning);
      }
    } catch (err) {
      console.error('上書き保存エラー:', err);
      const message = extractErrorMessage(err, '上書きの保存に失敗しました');
      setError(message);
      toast.error(message);
    } finally {
      setSavingItemId(null);
    }
  };

  const clearOverride = async (item: ResolvedItem) => {
    const overrideId = ownOverrideId(item);
    if (!overrideId) return;

    setSavingItemId(item.item_id);
    setError(null);
    try {
      const response = await api.delete(`/evaluation-item-overrides/${overrideId}`);
      toast.success('上書きを解除しました');
      await fetchResolvedSet();
      if (response.data.weight_warning) {
        setError(response.data.weight_warning);
      }
    } catch (err) {
      console.error('上書き解除エラー:', err);
      const message = extractErrorMessage(err, '上書きの解除に失敗しました');
      setError(message);
      toast.error(message);
    } finally {
      setSavingItemId(null);
    }
  };

  const scopeOptions =
    scopeType === 'job_grade'
      ? JOB_GRADES.map((grade) => ({ value: grade, label: grade }))
      : jobTitles.map((jt) => ({
          value: jt.job_title_name,
          label: `${jt.job_title_name}（${jt.job_grade}）`
        }));

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          <strong>職級・役職別の上書き:</strong> 部門共通の項目セットを土台に、職級や役職ごとに配点と評価対象を変更します。
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          ※ 解決順序は「部門共通 → 職級 → 役職」で、より具体的な指定が優先されます。上書きしていない項目は上位の値をそのまま使います。
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          ※ 設定内容は評価ルートを組んだ時点で各被評価者の評価基準として確定します。確定済みの評価には影響しません。
        </Typography>
      </Alert>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="scope-type-label">適用範囲</InputLabel>
            <Select
              labelId="scope-type-label"
              label="適用範囲"
              value={scopeType}
              onChange={(e) => {
                setScopeType(e.target.value as ScopeType);
                setScopeValue('');
                setResolvedSet(null);
              }}
            >
              <MenuItem value="job_title">役職ごと</MenuItem>
              <MenuItem value="job_grade">職級ごと</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel id="scope-value-label">
              {scopeType === 'job_grade' ? '職級' : '役職'}
            </InputLabel>
            <Select
              labelId="scope-value-label"
              label={scopeType === 'job_grade' ? '職級' : '役職'}
              value={scopeValue}
              onChange={(e) => setScopeValue(e.target.value)}
            >
              {scopeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {scopeOptions.length === 0 && (
              <FormHelperText>
                この部署タイプで使える役職が役職マスタに登録されていません
              </FormHelperText>
            )}
          </FormControl>

          {resolvedSet && (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', ml: 'auto', flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  目標必須
                </Typography>
                <Typography variant="h5">
                  {resolvedSet.items.filter((i) => i.is_enabled && i.requires_goal).length} 項目
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  配点合計
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: resolvedSet.total_weight === 100 ? 'success.main' : 'error.main',
                    fontWeight: 'bold'
                  }}
                >
                  {resolvedSet.total_weight} 点
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  評価対象
                </Typography>
                <Typography variant="h5">{resolvedSet.enabled_items} 項目</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  部門共通の合計
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {resolvedSet.base_total_weight} 点
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {scopeType === 'job_title' && resolvedSet?.job_grade && (
        <Alert severity="info" sx={{ mb: 2 }}>
          役職「{resolvedSet.scope_value}」は職級「{resolvedSet.job_grade}」に属します。職級で設定した上書きも適用されています。
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !resolvedSet ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {scopeType === 'job_grade' ? '職級' : '役職'}を選択すると、その適用範囲での項目セットが表示されます
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>観点</TableCell>
                <TableCell>項目No</TableCell>
                <TableCell>評価項目</TableCell>
                <TableCell align="center">部門共通の配点</TableCell>
                <TableCell align="center">この範囲の配点</TableCell>
                <TableCell align="center">配点の決定元</TableCell>
                <TableCell align="center">目標・達成基準</TableCell>
                <TableCell align="center">評価対象</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resolvedSet.items.map((item) => {
                const overrideId = ownOverrideId(item);
                const saving = savingItemId === item.item_id;
                return (
                  <TableRow key={item.item_id} hover sx={{ opacity: item.is_enabled ? 1 : 0.55 }}>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        観点{item.category_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.item_number}</TableCell>
                    <TableCell>
                      {item.item_description}
                      {item.is_free_item && (
                        <Chip label="フリー項目" size="small" color="info" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {item.base_weight}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Select
                        size="small"
                        value={item.weight}
                        disabled={saving || !item.is_enabled}
                        onChange={(e) => saveOverride(item, { weight: Number(e.target.value) })}
                        sx={{ minWidth: 80 }}
                      >
                        {ALLOWED_WEIGHTS.map((w) => (
                          <MenuItem key={w} value={w}>
                            {w}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={SOURCE_LABELS[item.weight_source]}
                        size="small"
                        color={item.weight_source === 'base' ? 'default' : 'primary'}
                        variant={item.weight_source === 'base' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Select
                        size="small"
                        value={ownRequiresGoal(item) === null ? 'inherit' : String(item.requires_goal)}
                        disabled={saving}
                        onChange={(e) =>
                          saveOverride(item, {
                            requires_goal: e.target.value === 'inherit' ? null : e.target.value === 'true'
                          })
                        }
                        sx={{ minWidth: 130 }}
                      >
                        <MenuItem value="inherit">
                          継承（{item.base_requires_goal ? '必須' : '任意'}）
                        </MenuItem>
                        <MenuItem value="true">必須</MenuItem>
                        <MenuItem value="false">任意</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={item.is_enabled}
                        disabled={saving}
                        onChange={(e) => saveOverride(item, { is_enabled: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          overrideId
                            ? 'この範囲の上書きを解除して上位階層の値に戻す'
                            : 'この範囲での上書きはありません'
                        }
                      >
                        <IconButton
                          size="small"
                          disabled={!overrideId || saving}
                          onClick={() => clearOverride(item)}
                        >
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ItemOverridePanel;
